/**
 * Les nouveautés montrées au parent après une mise à jour (#408).
 *
 * Une entrée par version qui a quelque chose à dire au parent. Une version
 * sans entrée (un correctif) ne montre rien. Les textes vivent dans l'i18n,
 * sous `whatsNew.<id>.<item>.title` et `.body`.
 *
 * Qui la voit : un parent dont le foyer existait AVANT la version annoncée.
 * Une famille qui découvre l'app dans cette version n'a rien à « redécouvrir »
 * (households.created_on_app_version, posé à la création depuis la 3.0.0 ;
 * null pour les foyers plus anciens, qui la voient donc). Une seule fois par
 * appareil : la dernière version vue est gardée dans le stockage local.
 */
import { compareVersions } from '@/models/AppVersion'

export interface WhatsNewItem {
  /** Clé sous `whatsNew.<id>`. */
  key: string
  /** Icône Lucide (cf. icon.service), en grand quand la page n'a pas d'image. */
  icon: string
}

export interface WhatsNewRelease {
  /** La version qui l'annonce : elle s'affiche à partir de celle-ci. */
  version: string
  /** Clé i18n de l'entrée, sous `whatsNew` (et le libellé de version affiché). */
  id: string
  items: WhatsNewItem[]
  /**
   * Les langues qui ont leurs captures d'écran, une par nouveauté, servies
   * depuis `public/` (cf. whatsNewImage). Une capture montre l'interface dans
   * sa langue : une langue absente de la liste garde l'icône plutôt que de
   * voir l'app en français.
   */
  imageLocales?: string[]
}

/**
 * Du plus récent au plus ancien. La 3.1.1 annonce aussi la 3.1.0, qui n'est
 * jamais sortie chez les familles (TestFlight seulement).
 */
export const WHATS_NEW: WhatsNewRelease[] = [
  {
    version: '3.1.1',
    id: 'v3_1',
    items: [
      { key: 'phone', icon: 'smartphone' },
      { key: 'cheer', icon: 'sticker' },
      { key: 'rhythm', icon: 'repeat' },
      { key: 'away', icon: 'backpack' },
      { key: 'party', icon: 'party-popper' },
    ],
    imageLocales: ['fr'],
  },
]

/** La capture d'une nouveauté dans la langue du parent, ou `null` (l'icône). */
export function whatsNewImage(release: WhatsNewRelease, item: WhatsNewItem, locale: string): string | null {
  if (!release.imageLocales?.includes(locale)) return null
  return `/img/whats-new/${release.id}/${locale}/${item.key}.webp`
}

/**
 * L'entrée à montrer, ou `null`.
 *
 * La plus récente parmi celles parues jusqu'à `current`, à condition qu'elle
 * soit plus récente que la dernière vue sur cet appareil ET que la version qui
 * a créé le foyer.
 */
export function pickWhatsNew(args: {
  releases?: WhatsNewRelease[]
  current: string
  lastSeen: string | null
  householdCreatedOn: string | null | undefined
}): WhatsNewRelease | null {
  const { releases = WHATS_NEW, current, lastSeen, householdCreatedOn } = args
  const floors = [lastSeen, householdCreatedOn].filter((v): v is string => !!v)
  const candidates = releases
    .filter(r => compareVersions(r.version, current) <= 0)
    .filter(r => floors.every(floor => compareVersions(r.version, floor) > 0))
    .sort((a, b) => compareVersions(b.version, a.version))
  return candidates[0] ?? null
}

/**
 * Les nouveautés de la version installée, pour les rouvrir à la demande
 * (Réglages) : la plus récente parue jusqu'à `current`, vue ou non.
 */
export function latestWhatsNew(current: string, releases: WhatsNewRelease[] = WHATS_NEW): WhatsNewRelease | null {
  return pickWhatsNew({ releases, current, lastSeen: null, householdCreatedOn: null })
}

const STORAGE_KEY = 'harmonia:whatsNewSeen'

export function readWhatsNewSeen(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export function markWhatsNewSeen(version: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, version)
  } catch {
    // Stockage refusé : au pire, la modale reviendra. Rien ne doit casser.
  }
}
